import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faCheck,
  faHourglassStart,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  ReferralState as ReferralStateEnum,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  BodyM,
  HeadlineL,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { ClaimReferralRewardsDialog } from '@sorare/core/src/components/referral/ClaimReferralRewardsDialog';
import {
  ExpiredReferralStatusAction,
  ProgressionBar,
} from '@sorare/core/src/components/referral/RefereesList/RefereeItem/ReferralStatus';
import Avatar from '@sorare/core/src/components/user/Avatar';
import UserName from '@sorare/core/src/components/user/UserName';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useReferralRewards } from '@sorare/core/src/hooks/referral/useReferralRewards';
import useAmountWithConversion from '@sorare/core/src/hooks/useAmountWithConversion';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import useToggle from '@sorare/core/src/hooks/useToggle';
import { glossary, referralStateMessages } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import RightBlock, {
  Body,
  Placeholder,
  ResponsiveNumber,
} from 'components/invite/OnlineLanding/RightBlock';

import {
  ReferralItem_referral,
  ReferralListQuery,
  ReferralListQueryVariables,
  useReferralState_referral,
} from './__generated__/index.graphql';

const messages = defineMessages({
  title: {
    defaultMessage: 'Referral hub',
    id: 'invite.Hub.title2',
  },
  subtitle: {
    defaultMessage: 'Track your friends progression',
    id: 'invite.Hub.subTitle',
  },
});

const ReferralList = styled(Vertical).attrs({ gap: 2 })`
  align-self: stretch;
`;

const StyledResponsiveNumber = styled(ResponsiveNumber)`
  max-height: 380px;
`;

const StyledBody = styled(Body)`
  padding: 0 var(--quadruple-unit) var(--quadruple-unit) var(--quadruple-unit);

  & > * {
    width: 100%;
  }
`;

export enum ComputedReferralState {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  EXPIRED = 'EXPIRED',
  NOT_CONFIRMED = 'NOT_CONFIRMED',
}

const useReferralState = ({
  referral,
}: {
  referral: useReferralState_referral;
}) => {
  if (referral.aasmState === 'expired') return ComputedReferralState.EXPIRED;
  if (referral.completedAt) return ComputedReferralState.COMPLETED;
  if (referral.refereeInvitationSentAt && !referral.refereeConfirmedAt)
    return ComputedReferralState.NOT_CONFIRMED;
  return ComputedReferralState.IN_PROGRESS;
};

useReferralState.fragments = {
  referral: gql`
    fragment useReferralState_referral on Referral {
      id
      aasmState
      completedAt
      refereeInvitationSentAt
      refereeConfirmedAt
      refereeSportCardsBoughtFromPrimaryMarketCount(sport: $sport)
      referrerRewards {
        id
      }
    }
  ` as TypedDocumentNode<useReferralState_referral>,
};

const ReferralState = ({ state }: { state: ComputedReferralState }) => {
  if (state === ComputedReferralState.EXPIRED)
    return (
      <FormattedMessage id="ReferralState.expired" defaultMessage="Expired" />
    );
  if (state === ComputedReferralState.COMPLETED)
    return (
      <FormattedMessage
        id="ReferralState.completed"
        defaultMessage="Completed"
      />
    );
  return (
    <FormattedMessage
      id="ReferralState.inProgress"
      defaultMessage="In progress"
    />
  );
};

const ReferralStateIcon = ({ state }: { state: ComputedReferralState }) => {
  if (state === ComputedReferralState.EXPIRED)
    return <FontAwesomeIcon color="var(--c-red-300)" icon={faXmark} />;
  if (state === ComputedReferralState.COMPLETED)
    return <FontAwesomeIcon color="var(--c-green-600)" icon={faCheck} />;
  return <FontAwesomeIcon color="var(--c-brand-300)" icon={faHourglassStart} />;
};

const ReferralStatus = ({
  referral,
  cardsRequirements,
  referralProgramConfigAmount,
}: {
  referral: ReferralItem_referral;
  cardsRequirements: number;
  referralProgramConfigAmount: string;
}) => {
  const referralState = useReferralState({ referral });
  const cardsBought = referral.refereeSportCardsBoughtFromPrimaryMarketCount;
  const conversionCreditWon = referral.referrerRewards.find(
    referrerReward => !!referrerReward.conversionCredit
  )?.conversionCredit;
  const { main } = useAmountWithConversion({
    monetaryAmount: conversionCreditWon?.maxDiscount || zeroMonetaryAmount,
  });

  if (referralState === ComputedReferralState.EXPIRED) {
    return <ExpiredReferralStatusAction id={referral.id} />;
  }

  const progression = (
    <ProgressionBar
      cardsBought={cardsBought}
      cardsRequirements={cardsRequirements}
    />
  );

  if (referralState === ComputedReferralState.COMPLETED) {
    const hasWonConversionCredit = !!conversionCreditWon;
    const hasWonCard = !!referral.referrerRewards.find(
      referrerReward => !!referrerReward.anyCard
    );
    return (
      <Vertical gap={4} center>
        {progression}
        {referral.referrerRewards.length > 0 && (
          <>
            {hasWonConversionCredit && hasWonCard ? (
              <BodyM color="var(--c-neutral-600)">
                <FormattedMessage
                  id="ReferralItem.cardAndCreditWon"
                  defaultMessage="Congratulations! You won {amount} and a Card"
                  values={{
                    amount: main,
                  }}
                />
              </BodyM>
            ) : (
              <>
                {hasWonConversionCredit && (
                  <BodyM color="var(--c-neutral-600)">
                    <FormattedMessage
                      id="ReferralItem.creditWon"
                      defaultMessage="Congratulations! You won {amount}"
                      values={{
                        amount: main,
                      }}
                    />
                  </BodyM>
                )}
                {hasWonCard && (
                  <BodyM color="var(--c-neutral-600)">
                    <FormattedMessage
                      id="ReferralItem.cardWon"
                      defaultMessage="Congratulations! You won a card"
                    />
                  </BodyM>
                )}
              </>
            )}
          </>
        )}
      </Vertical>
    );
  }

  return (
    <Vertical gap={4}>
      {progression}
      <BodyM color="var(--c-neutral-600)">
        <FormattedMessage
          id="ReferralItem.cardsLeft"
          defaultMessage="Only {count, plural, one {# Card} other {# Cards}} to go to receive {amount} credits!"
          values={{
            count: Math.max(cardsRequirements - cardsBought, 0),
            amount: referralProgramConfigAmount,
          }}
        />
      </BodyM>
    </Vertical>
  );
};

const StyledButtonBase = styled(ButtonBase)`
  text-align: left;
`;

const StyledTabBar = styled(TabBar)`
  --bar-bg-color-custom: 'transparent';
`;

const ReferralItem = ({
  referral,
  referralProgramConfigAmount,
  cardsRequirements,
}: {
  referral: ReferralItem_referral;
  referralProgramConfigAmount: string;
  cardsRequirements: number;
}) => {
  const [open, toggle] = useToggle(false);
  const referralState = useReferralState({ referral });
  const { isClaimed } = useReferralRewards(referral.referrerRewards);
  const hasUnclaimedRewards = referral.referrerRewards.some(
    reward => !isClaimed(reward)
  );
  const [openClaimDialog, toggleOpenClaimDialog] = useToggle(false);

  return (
    <>
      <StyledButtonBase
        onClick={hasUnclaimedRewards ? toggleOpenClaimDialog : toggle}
      >
        <Horizontal key={referral.id}>
          <Avatar user={referral.referee} rounded />
          <Vertical grow gap={0}>
            <UserName user={referral.referee} />
            <LabelM color="var(--c-neutral-600)">
              <ReferralState state={referralState} />
            </LabelM>
          </Vertical>
          {hasUnclaimedRewards ? (
            <Button key={referral.id} size="small" color="primary" asSpan>
              <FormattedMessage {...glossary.claim} />
            </Button>
          ) : (
            <ChevronRightBold />
          )}
        </Horizontal>
      </StyledButtonBase>
      <ClaimReferralRewardsDialog
        referralRewards={referral.referrerRewards}
        open={openClaimDialog}
        onClose={toggleOpenClaimDialog}
      />
      <Dialog maxWidth="sm" open={open} onClose={toggle}>
        <StyledBody center>
          <Avatar size={10} user={referral.referee} rounded />
          <Vertical center>
            <LabelL color="var(--c-neutral-600)">
              <Horizontal center>
                <ReferralStateIcon state={referralState} />
                <ReferralState state={referralState} />
              </Horizontal>
            </LabelL>
            <HeadlineL>
              <UserName user={referral.referee} />
            </HeadlineL>
          </Vertical>
          <ReferralStatus
            referral={referral}
            cardsRequirements={cardsRequirements}
            referralProgramConfigAmount={referralProgramConfigAmount}
          />
        </StyledBody>
      </Dialog>
    </>
  );
};

ReferralItem.fragments = {
  referral: gql`
    fragment ReferralItem_referral on Referral {
      id
      aasmState
      completedAt
      referee {
        slug
        ...UserName_publicUserInfoInterface
        ...Avatar_user
      }
      refereeSportCardsBoughtFromPrimaryMarketCount(sport: $sport)
      referrerRewards {
        id
        anyCard {
          slug
        }
        conversionCredit {
          id
          maxDiscount {
            ...MonetaryAmountFragment_monetaryAmount
          }
        }
        ...useReferralRewards_referralReward
        ...ClaimReferralRewardsDialog_referralReward
      }
      ...useReferralState_referral
    }
    ${UserName.fragments.user}
    ${Avatar.fragments.user}
    ${useReferralState.fragments.referral}
    ${monetaryAmountFragment}
    ${useReferralRewards.fragments.referralReward}
    ${ClaimReferralRewardsDialog.fragments.referralReward}
  ` as TypedDocumentNode<ReferralItem_referral>,
};

const REFERRAL_LIST_QUERY = gql`
  query ReferralListQuery($page: Int, $sport: Sport!, $state: ReferralState) {
    currentUser {
      slug
      referrals(sport: $sport, pageSize: 10, page: $page, state: $state) {
        nodes {
          id
          ...ReferralItem_referral
        }
        currentPage
        pages
        totalCount
      }
    }
  }
  ${ReferralItem.fragments.referral}
` as TypedDocumentNode<ReferralListQuery, ReferralListQueryVariables>;

const ReferralsList = ({
  sport,
  referralProgramConfigAmount,
  cardsRequirements,
}: {
  sport: Sport;
  referralProgramConfigAmount: string;
  cardsRequirements: number;
}) => {
  const [referralState, setReferralState] = useState<ReferralStateEnum>(
    ReferralStateEnum.ALL
  );
  const { formatMessage } = useIntlContext();

  const selectOptions = [
    ReferralStateEnum.ALL,
    ReferralStateEnum.IN_PROGRESS,
    ReferralStateEnum.COMPLETED,
    ReferralStateEnum.EXPIRED,
  ].map(element => ({
    value: element,
    label: formatMessage(referralStateMessages[element]),
  }));

  const { data, loadMore, loading } = usePaginatedQuery(REFERRAL_LIST_QUERY, {
    variables: { sport, state: referralState },
    connection: 'ReferralPaginated',
  });

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        sport,
        state: referralState,
        page: (data?.currentUser?.referrals.currentPage || 0) + 1,
      });
    },
    (data?.currentUser?.referrals.currentPage || 0) <
      (data?.currentUser?.referrals.pages || 1) - 1,
    loading
  );

  return (
    <Vertical gap={3}>
      <StyledTabBar
        variant="flat"
        value={referralState}
        fullWidth
        forceMobileLayout
      >
        {selectOptions.map(s => (
          <Tab
            key={s.value}
            value={s.value}
            onClick={() => setReferralState(s.value)}
          >
            {s.label}
          </Tab>
        ))}
      </StyledTabBar>
      <ReferralList>
        {data?.currentUser?.referrals.nodes.map(referral => {
          return (
            <ReferralItem
              key={referral.id}
              referral={referral}
              referralProgramConfigAmount={referralProgramConfigAmount}
              cardsRequirements={cardsRequirements}
            />
          );
        })}
        <InfiniteScrollLoader small />
      </ReferralList>
    </Vertical>
  );
};

const Hub = ({
  total,
  sport,
  referralProgramConfigAmount,
  cardsRequirements,
}: {
  total?: number;
  sport: Sport;
  referralProgramConfigAmount: string;
  cardsRequirements: number;
}) => {
  const loading = typeof total === 'undefined';

  return (
    <RightBlock
      title={
        <FormattedMessage
          id="invite.Hub.title"
          defaultMessage="Referral hub ({total})"
          values={{
            total: <Skeleton loading={loading}>{total || '000'}</Skeleton>,
          }}
        />
      }
      subTitle={<FormattedMessage {...messages.subtitle} />}
      total={`${total || 0}`}
      dialogProps={{
        title: <FormattedMessage {...messages.title} />,
        subtitle: <FormattedMessage {...messages.subtitle} />,
        children:
          total && total > 0 ? (
            <ReferralsList
              sport={sport}
              referralProgramConfigAmount={referralProgramConfigAmount}
              cardsRequirements={cardsRequirements}
            />
          ) : (
            <Placeholder>
              <StyledResponsiveNumber total="0" alignmentBaseline="middle" />
            </Placeholder>
          ),
      }}
      loading={loading}
    />
  );
};

export default Hub;
